import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { collectTopicsUnder } from '../../courses/preview/TopicPreviewPanel';
import * as coursesApi from '../../courses/api';
import * as exerciseApi from '../../exercise-templates/api';
import { getNotesForWorkspace } from '../../students/workspace/notesApi';
import wsManager from '../../../services/wsManager';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';

function noteTargetKey(kind, id) {
   return kind + ':' + id;
}

// Same grouping as useStudentWorkspaceData.js's groupNotesByTarget - notes
// are fetched from the representative workspace only (they're identical
// across every group member's workspace once the backend has propagated
// them, so fetching N times would just be redundant network calls).
function groupNotesByTarget(notes) {
   const map = {};
   notes.forEach(note => {
      let key = null;
      if (note.topicElement) key = noteTargetKey('topicElement', note.topicElement.id);
      else if (note.exerciseElement) key = noteTargetKey('exerciseElement', note.exerciseElement.id);
      else if (note.exerciseInteractiveElement) key = noteTargetKey('exerciseInteractiveElement', note.exerciseInteractiveElement.id);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(note);
   });
   return map;
}

function fetchExerciseContent(exerciseTemplateId) {
   return Promise.all([
      exerciseApi.getElements(exerciseTemplateId),
      exerciseApi.getInteractiveElements(exerciseTemplateId),
      exerciseApi.getContainers(exerciseTemplateId),
   ]).then(([elementsRes, interactiveRes, containersRes]) => ({
      elements: Array.isArray(elementsRes.data) ? elementsRes.data : [],
      interactiveElements: Array.isArray(interactiveRes.data) ? interactiveRes.data : [],
      containers: Array.isArray(containersRes.data) ? containersRes.data : [],
   }));
}

function fetchTopicContent(topic) {
   return Promise.all([
      coursesApi.getList(TOPIC_ELEMENT_BASE, 'topic', topic.id),
      coursesApi.getList(TOPIC_CONTAINER_BASE, 'topic', topic.id),
      coursesApi.getTopicExerciseTemplates(topic.id),
   ]).then(([elementsRes, containersRes, linksRes]) => {
      const elements = Array.isArray(elementsRes.data) ? elementsRes.data : [];
      const containers = Array.isArray(containersRes.data) ? containersRes.data : [];
      const links = Array.isArray(linksRes.data) ? linksRes.data : [];
      return Promise.all(links.map(link => (
         link.exerciseTemplate
            ? fetchExerciseContent(link.exerciseTemplate.id).then(content => ({ link, ...content }))
            : Promise.resolve(null)
      ))).then(exerciseLinks => ({
         topic, elements, containers,
         exerciseLinks: exerciseLinks.filter(Boolean),
      }));
   });
}

// One member's own personal activity slice - deliberately NOT shared/merged
// data (per the architecture: exercises/answers stay 100% personal to each
// student, only canvas+notes are group-synced) - fetched per member so the
// group view can show "who answered what" without touching any student's
// own progress. Takes a bare workspaceId (GroupCourseMemberWorkspace is a
// flat {studentId, name, workspaceId} DTO, not a nested workspace object).
function fetchMemberActivity(workspaceId, studentId, topics) {
   return Promise.all([
      axios.get('/api/studentconversationprogresses?workspace=' + workspaceId, { withCredentials: true }),
      axios.get('/api/exercisestudentsavedinteractiveelements?student=' + studentId, { withCredentials: true }),
   ]).then(([progressesRes, savedStatesRes]) => {
      const interactiveElementIds = new Set();
      topics.forEach(t => t.exerciseLinks.forEach(ex => {
         ex.interactiveElements.forEach(el => interactiveElementIds.add(el.id));
      }));

      const savedStates = (Array.isArray(savedStatesRes.data) ? savedStatesRes.data : [])
         .filter(s => s.interactiveElement && interactiveElementIds.has(s.interactiveElement.id));
      const savedStateByElementId = {};
      savedStates.forEach(s => { savedStateByElementId[s.interactiveElement.id] = s; });

      const progresses = Array.isArray(progressesRes.data) ? progressesRes.data : [];
      return Promise.all(progresses.map(progress => (
         axios.get('/api/studentconversationprogresses/' + progress.id + '/steps', { withCredentials: true })
            .then(stepsRes => ({ progress, steps: Array.isArray(stepsRes.data) ? stepsRes.data : [] }))
      ))).then(progressWithSteps => {
         const progressByElementId = {};
         progressWithSteps.forEach(p => {
            if (p.progress.interactiveElement) progressByElementId[p.progress.interactiveElement.id] = p;
         });
         return { savedStateByElementId, progressByElementId };
      });
   });
}

// Group-wide counterpart of useStudentWorkspaceData.js - same course
// structure (shared/linked, fetched once, not per member), but activity
// (saved states/conversation progress) is fetched PER MEMBER and kept
// separate (never merged into one blob) so GroupWorkspaceTopicSection.jsx
// can render "which member answered what" without losing whose answer is
// whose. Notes/canvas are shared (backend-propagated), so notes are read
// once from the representative workspace and canvas is never fetched here
// at all - it's opened as its own page (TopicCanvasView.jsx), same route a
// single student's workspace already uses, just pointed at whichever
// member the backend designates as representative (any of them - they're
// kept in sync).
//
// `members` (from GET /api/groups/{groupId}/courses/{courseId}/workspace)
// is a flat DTO list - {studentId, name, workspaceId} per
// GroupCourseMemberWorkspace.java, NOT a nested {student, workspace} shape -
// `workspaceId` is null for a member who's never opened the course.
export default function useGroupWorkspaceData(groupId, courseId) {
   const [state, setState] = useState({ status: 'loading' });

   const refreshNotes = () => {
      if (state.status !== 'ready' || !state.representativeWorkspaceId) return;
      getNotesForWorkspace(state.representativeWorkspaceId).then(notesRes => {
         const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
         setState(current => (current.status === 'ready'
            ? { ...current, notesByTarget: groupNotesByTarget(notes), lastLiveUpdateAt: Date.now() }
            : current));
      }).catch(() => {});
   };
   const refreshNotesRef = useRef(refreshNotes);
   refreshNotesRef.current = refreshNotes;

   const refreshActivity = () => {
      if (state.status !== 'ready') return;
      Promise.all(state.members.filter(m => m.workspaceId).map(m => (
         fetchMemberActivity(m.workspaceId, m.studentId, state.topics).then(slice => ({ studentId: m.studentId, slice }))
      ))).then(results => {
         const activityByStudentId = {};
         results.forEach(r => { activityByStudentId[r.studentId] = r.slice; });
         setState(current => (current.status === 'ready'
            ? { ...current, activityByStudentId, lastLiveUpdateAt: Date.now() }
            : current));
      }).catch(() => {});
   };
   const refreshActivityRef = useRef(refreshActivity);
   refreshActivityRef.current = refreshActivity;

   useEffect(() => {
      if (!groupId || !courseId) return;
      let cancelled = false;
      setState(prev => (prev.status === 'ready' ? prev : { status: 'loading' }));

      Promise.all([
         axios.get('/api/groups/' + groupId + '/courses/' + courseId + '/workspace', { withCredentials: true }),
         coursesApi.getOne('/api/courses', courseId),
      ])
         .then(([workspaceRes, courseRes]) => {
            if (cancelled) return;
            const data = workspaceRes.data || {};
            const course = courseRes.data;
            const members = Array.isArray(data.members) ? data.members : [];
            const activeMembers = members.filter(m => m.workspaceId);
            if (activeMembers.length === 0) {
               setState({ status: 'not-started', members, course });
               return;
            }
            const representativeWorkspaceId = data.representativeWorkspaceId || activeMembers[0].workspaceId;

            collectTopicsUnder('course', courseId).then(topicList => (
               Promise.all(topicList.map(fetchTopicContent)).then(topics => (
                  Promise.all([
                     getNotesForWorkspace(representativeWorkspaceId),
                     Promise.all(activeMembers.map(m => (
                        fetchMemberActivity(m.workspaceId, m.studentId, topics).then(slice => ({ studentId: m.studentId, slice }))
                     ))),
                  ]).then(([notesRes, activityResults]) => {
                     if (cancelled) return;
                     const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
                     const activityByStudentId = {};
                     activityResults.forEach(r => { activityByStudentId[r.studentId] = r.slice; });

                     setState({
                        status: 'ready',
                        courseId,
                        course,
                        members,
                        topics,
                        activityByStudentId,
                        notesByTarget: groupNotesByTarget(notes),
                        representativeWorkspaceId,
                        lastLiveUpdateAt: null,
                     });
                  })
               ))
            ));
         })
         .catch(() => { if (!cancelled) setState({ status: 'error' }); });

      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [groupId, courseId]);

   // Live updates - subscribe to EVERY active member's own workspace topic
   // (not just the representative one) since a note/canvas change can be
   // mirrored-in via any member's workspace depending on who triggered it
   // server-side, and an activity change (answer_saved) is naturally
   // per-member anyway. Notes refetch from the representative workspace
   // regardless of which member's topic fired the event, since they're
   // kept identical by the backend.
   useEffect(() => {
      if (state.status !== 'ready') return undefined;
      const ids = state.members.filter(m => m.workspaceId).map(m => m.workspaceId);
      const handlers = ids.map(id => {
         const topic = '/topic/workspace/' + id;
         const handler = (event) => {
            if (event.type === 'answer_saved' || event.type === 'conversation_step') {
               refreshActivityRef.current();
            } else if (event.type === 'note_added' || event.type === 'note_updated' || event.type === 'note_deleted') {
               refreshNotesRef.current();
            }
         };
         wsManager.subscribe(topic, handler);
         return { topic, handler };
      });
      return () => { handlers.forEach(h => wsManager.unsubscribe(h.topic, h.handler)); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.status === 'ready' ? state.members.map(m => m.workspaceId).join(',') : null]);

   return { ...state, refreshNotes, refreshActivity };
}
